import React, { useCallback, useEffect, useRef, useState } from 'react';

import { BookingConfirmed } from 'components/payment/receipt/components/booking-confirmed';
import { ReceiptDeciding } from 'components/payment/receipt/components/receipt-deciding';
import { StyledModalWrapper } from 'components/payment/receipt/styles';
import { ADD_ONS_DECLINED_PARAM, useBookingAddOnsOffer } from 'hooks/use-booking-add-ons-offer.hook';
import { usePostBookingLanding } from 'hooks/use-post-booking-landing.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useSetStepHeader } from 'pages/booking-add-ons/step-header.context';
import { useCompletedBooking } from './use-completed-booking.hook';

/*
 * How long the decision may run before its wait is drawn. The common case -- the booking was fetched by
 * the checkout and the add-on menu answers quickly -- decides inside this, so the tenant goes from the
 * checkout straight to My bookings or to Booking confirmed, with no screen in between.
 */
export const DECIDING_PLACEHOLDER_DELAY_MS = 400;

interface BookingCompletedProps {
  /* PaymentContent's door into the add-on step, from this route's own path (`…/booked`). */
  onBrowseAddOns: (bookingId: string) => void;
}

/**
 * RES-1407 — where every checkout lands under the 2-way chat redesign, in place of the old receipt.
 *
 * It decides between two screens and draws neither the receipt nor its modal:
 *   - the booking's room offers add-ons → Booking confirmed, the add-on offer, same rules as before
 *     (`useBookingAddOnsOffer`: native, `add_ons_enabled`, a live status, an orderable item);
 *   - otherwise → My bookings with the booking selected (`usePostBookingLanding`).
 *
 * ⭐ EVERY EXIT FROM BOOKING CONFIRMED LANDS: Done, the booking summary tile, the modal's corner X and the
 * mobile shell's close. The add-on step's ways out come back through here too (`addOnsExitPath` points
 * at this segment): its back arrow finds the offer still standing and shows Booking confirmed again;
 * "Continue without add-ons" carries the declined marker and lands; a refused booking finds no offer and
 * lands. Ordering ends on the add-on step's own View booking, which lands directly.
 *
 * ⚠️ Every checkout reaches this route: quick checkout, checkout, the multi-day hook, both 3DS forms and
 * the 3DS redirect (`useBookingCompletionSegment`). A multi-day purchase decides and lands on its FIRST
 * booking, the one its cart carries.
 */
export const BookingCompleted = ({ onBrowseAddOns }: BookingCompletedProps): JSX.Element | null => {
  const searchParams = useSearchParams<Record<string, string | undefined>>();
  const { uuid, transaction } = useCompletedBooking();
  const { shouldOffer, bookingId, isDeciding } = useBookingAddOnsOffer(transaction);
  const landOnMyBookings = usePostBookingLanding();

  /*
   * Latched per booking, as the receipt did: the wait is bounded, and a late answer must not swap the
   * screen under the tenant. ⛔ An unlatched render counts as still deciding -- the first render after
   * the answer has `isDeciding` false and no latch yet, and reading that as "no offer" would land in the
   * one render before Booking confirmed appears.
   */
  const [decided, setDecided] = useState<{ uuid?: string; offer: boolean } | null>(null);
  const decidedOffer = decided && decided.uuid === transaction?.uuid ? decided.offer : null;

  useEffect(() => {
    if (decidedOffer === null && !isDeciding) {
      setDecided({ uuid: transaction?.uuid, offer: shouldOffer });
    }
  }, [decidedOffer, isDeciding, shouldOffer, transaction?.uuid]);

  const declinedAddOns = searchParams?.[ADD_ONS_DECLINED_PARAM] === 'true';
  const showsOffer = decidedOffer === true && Boolean(bookingId) && !declinedAddOns;
  const landsNow = declinedAddOns || decidedOffer === false || (decidedOffer === true && !bookingId);

  const landOnBooking = useCallback(
    () =>
      landOnMyBookings({
        transactionUuid: transaction?.uuid ?? uuid,
        bookingId: transaction?.details?.id,
        params: searchParams,
      }),
    [landOnMyBookings, transaction, uuid, searchParams],
  );

  /* Once: the landing unmounts this route, and a second call would push My bookings twice. */
  const hasLanded = useRef(false);
  const landOnce = useCallback(() => {
    if (!hasLanded.current) {
      hasLanded.current = true;
      landOnBooking();
    }
  }, [landOnBooking]);

  useEffect(() => {
    if (landsNow) {
      landOnce();
    }
  }, [landsNow, landOnce]);

  /* The mobile shell's own close goes where the modal's corner X does. */
  useSetStepHeader({ onClose: landOnce });

  /* Nothing for the common, fast decision; the confirmation's skeleton only if the wait is felt. */
  const [isWaitVisible, setIsWaitVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsWaitVisible(true), DECIDING_PLACEHOLDER_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  /*
   * ⛔ `withExitButton` is not optional: ModalWrapper defaults it off, and the deciding state has no
   * footer, so without it there is no way out of the wait. Both screens bring their own modal because
   * ReceiptBooking's desktop branch is a bare div.
   */
  if (showsOffer && bookingId && transaction) {
    return (
      <StyledModalWrapper
        visible
        withExitButton
        onClose={landOnce}
        content={
          <BookingConfirmed
            booking={transaction}
            bookingId={bookingId}
            onOpenReceipt={landOnce}
            onBrowseAddOns={onBrowseAddOns}
            onClose={landOnce}
          />
        }
      />
    );
  }

  if (!landsNow && isWaitVisible) {
    return <StyledModalWrapper visible withExitButton onClose={landOnce} content={<ReceiptDeciding />} />;
  }

  return null;
};
